import { useState, useEffect } from "react";
import axios from "axios";
import {
  Newspaper, Fish, Waves, Gauge, Coins, ArrowRight, ArrowLeft,
  Check, X, Radio, TrendingUp, Eye, ShieldCheck, CreditCard,
  UserCheck, ChevronDown
} from "lucide-react";
import { API } from "../constants";

function timeAgo(isoDate) {
  if (!isoDate) return "";
  const seconds = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000);
  if (seconds < 60) return "ahora";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.floor(hours / 24)} d`;
}

function formatUSD(value) {
  if (value === null || value === undefined) return "--";
  const abs = Math.abs(value);
  if (abs >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

/* ---------- Vista previa EN VIVO (datos reales de la API) ---------- */
function LivePreview() {
  const [news, setNews] = useState([]);
  const [whales, setWhales] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [n, w] = await Promise.all([
          axios.get(`${API}/news`, { params: { limit: 4 }, timeout: 15000 }),
          axios.get(`${API}/whales/summary`, { timeout: 20000 }),
        ]);
        setNews(n.data?.news || []);
        setWhales(w.data);
      } catch (e) {
        console.error("Preview error:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const fng = whales?.fear_greed;
  const trades = (whales?.large_trades || []).slice(0, 3);
  const flows = (whales?.defi_flows || []).slice(0, 3);
  const fngColor = !fng ? "text-gray-400" : fng.value <= 25 ? "text-red-400" : fng.value <= 45 ? "text-orange-400" : fng.value <= 55 ? "text-yellow-400" : fng.value <= 75 ? "text-lime-400" : "text-emerald-400";

  return (
    <div className="bg-[#0d0d0f] border border-gray-800 rounded-2xl p-5 md:p-6 shadow-2xl shadow-cyan-500/5" data-testid="live-preview">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-emerald-400 animate-pulse" />
          <span className="text-sm font-semibold text-white">Terminal en vivo</span>
        </div>
        <span className="text-xs text-gray-500">datos reales · ahora mismo</span>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="animate-pulse h-40 bg-gray-800/50 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {/* Sentimiento */}
          <div className="bg-gray-900/60 rounded-xl p-4 border border-gray-800/60">
            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> SENTIMIENTO</p>
            {fng ? (
              <>
                <p className={`text-4xl font-bold ${fngColor}`}>{fng.value}</p>
                <p className={`text-sm ${fngColor}`}>{fng.classification}</p>
                <div className="mt-3 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-emerald-500" style={{ width: `${fng.value}%` }} />
                </div>
                <p className="text-xs text-gray-500 mt-2">Fear & Greed Index</p>
              </>
            ) : <p className="text-gray-500 text-sm">Cargando…</p>}
            {whales?.stablecoin_flows?.net_flow_7d_usd !== undefined && (
              <div className="mt-4 pt-3 border-t border-gray-800/60">
                <p className="text-xs text-gray-500">Stablecoins 7d</p>
                <p className={`text-lg font-semibold ${whales.stablecoin_flows.net_flow_7d_usd >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {whales.stablecoin_flows.net_flow_7d_usd >= 0 ? "+" : ""}{formatUSD(whales.stablecoin_flows.net_flow_7d_usd)}
                </p>
              </div>
            )}
          </div>

          {/* Ballenas */}
          <div className="bg-gray-900/60 rounded-xl p-4 border border-gray-800/60">
            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1.5"><Fish className="w-3.5 h-3.5" /> BALLENAS</p>
            <div className="space-y-2.5">
              {trades.length > 0 ? trades.map((t, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className={t.side === "buy" ? "text-emerald-400" : "text-red-400"}>
                    {t.side === "buy" ? "▲" : "▼"} {t.symbol}
                  </span>
                  <span className="text-white font-medium">{formatUSD(t.value_usd)}</span>
                </div>
              )) : flows.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className={f.direction === "inflow" ? "text-emerald-400" : "text-red-400"}>
                    {f.direction === "inflow" ? "▲" : "▼"} {f.protocol}
                  </span>
                  <span className="text-white font-medium">{formatUSD(Math.abs(f.flow_1d_usd))}</span>
                </div>
              ))}
              {trades.length === 0 && flows.length === 0 && <p className="text-gray-500 text-sm">Sin movimientos destacados</p>}
            </div>
            <p className="text-xs text-gray-600 mt-3">grandes órdenes y flujos DeFi</p>
          </div>

          {/* Noticias */}
          <div className="bg-gray-900/60 rounded-xl p-4 border border-gray-800/60">
            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1.5"><Newspaper className="w-3.5 h-3.5" /> NOTICIAS</p>
            <div className="space-y-2.5">
              {news.slice(0, 3).map((n, i) => (
                <div key={i}>
                  <p className="text-sm text-gray-200 leading-snug line-clamp-2">{n.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{n.source} · {timeAgo(n.published_at)}</p>
                </div>
              ))}
              {news.length === 0 && <p className="text-gray-500 text-sm">Cargando titulares…</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Landing del Terminal ---------- */
function TerminalLandingPage({ onBack, onEnter, onRegister }) {
  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* NAV */}
      <header className="border-b border-gray-800 bg-[#0d0d0f]/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="TraceMetrics" className="h-9 w-auto" />
            <span className="text-xs font-bold tracking-widest text-cyan-400 border border-cyan-400/30 rounded px-2 py-0.5">TERMINAL</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-gray-400">
            <button onClick={() => scrollTo("herramientas")} className="hover:text-white transition-colors">Herramientas</button>
            <button onClick={() => scrollTo("rutina")} className="hover:text-white transition-colors">Rutina</button>
            <button onClick={() => scrollTo("planes")} className="hover:text-white transition-colors">Planes</button>
            <button onClick={() => scrollTo("faq")} className="hover:text-white transition-colors">FAQ</button>
          </nav>
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="text-gray-400 hover:text-white text-sm transition-colors hidden sm:block">Volver</button>
            <button onClick={onEnter} className="bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors" data-testid="nav-open-terminal">
              Abrir Terminal
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12 text-center">
        <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
          <Radio className="w-3.5 h-3.5 animate-pulse" /> TERMINAL CRIPTO · EN VIVO
        </div>
        <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
          Lee el mercado cripto
          <span className="block bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">antes de decidir.</span>
        </h1>
        <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-8">
          Noticias filtradas, movimientos de ballenas y flujos DeFi en una sola pantalla.
          Tu rutina de mercado en 5 minutos, no en 50 pestañas.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <button onClick={onEnter} className="bg-cyan-600 hover:bg-cyan-500 text-white font-semibold px-8 py-3.5 rounded-xl text-lg transition-colors flex items-center gap-2" data-testid="hero-cta">
            Acceso gratuito <ArrowRight className="w-5 h-5" />
          </button>
          <button onClick={() => scrollTo("planes")} className="border border-gray-700 hover:border-cyan-500 text-gray-300 hover:text-cyan-400 font-semibold px-8 py-3.5 rounded-xl text-lg transition-colors">
            Ver planes
          </button>
        </div>
        <div className="flex flex-wrap justify-center gap-2 text-xs text-gray-500">
          {["Noticias ES/EN", "Whale tracker", "Flujos DeFi", "Fear & Greed", "Sin tarjeta"].map((c) => (
            <span key={c} className="bg-gray-900 border border-gray-800 rounded-full px-3 py-1.5">{c}</span>
          ))}
        </div>
      </section>

      {/* LIVE PREVIEW */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-2">Una sola pantalla para leer la sesión</h2>
        <p className="text-gray-500 text-center mb-8">Esto no es una maqueta: son los datos reales del terminal, ahora mismo.</p>
        <LivePreview />
      </section>

      {/* HERRAMIENTAS */}
      <section id="herramientas" className="max-w-6xl mx-auto px-6 pb-20">
        <p className="text-cyan-400 text-sm font-semibold tracking-widest text-center mb-2">HERRAMIENTAS</p>
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Todo lo importante antes de una decisión</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: Newspaper, title: "Noticias multi-fuente", desc: "Titulares en español e inglés de DiarioBitcoin, CoinTelegraph y Decrypt, deduplicados y ordenados por hora. Actualización cada 10 minutos." },
            { icon: Fish, title: "Whale tracker", desc: "Grandes órdenes spot detectadas en MEXC, Binance y Kraken, filtradas por tamaño. Cuando una ballena se mueve, lo ves." },
            { icon: Waves, title: "Flujos DeFi", desc: "Entradas y salidas de TVL en los principales protocolos (datos DeFiLlama). El rastro del dinero inteligente, cada 24h." },
            { icon: Coins, title: "Stablecoin flows", desc: "Emisión y quema neta de stablecoins: la liquidez que entra o sale del mercado antes de que se note en el precio." },
            { icon: Gauge, title: "Fear & Greed", desc: "El termómetro del sentimiento del mercado en una mirada, para saber si la multitud está eufórica o asustada." },
            { icon: TrendingUp, title: "Tu análisis de trading", desc: "Conecta con tu dashboard TraceMetrics: tu PNL, tus operaciones y tu rendimiento junto al contexto del mercado." },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-6 hover:border-cyan-500/40 transition-colors">
              <Icon className="w-8 h-8 text-cyan-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">{title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RUTINA */}
      <section id="rutina" className="bg-[#0d0d0f] border-y border-gray-800">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-cyan-400 text-sm font-semibold tracking-widest text-center mb-2">RUTINA</p>
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Una rutina de mercado en 5 minutos</h2>
          <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">El Terminal está diseñado como un orden de lectura: sentimiento, ballenas, noticias y después tu decisión. Saber qué mirar antes de actuar.</p>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { n: "01", icon: Gauge, title: "Sentimiento", desc: "Empieza por el Fear & Greed y el flujo de stablecoins: ¿hay liquidez entrando o saliendo?" },
              { n: "02", icon: Fish, title: "Ballenas", desc: "Revisa las grandes órdenes y los flujos DeFi: ¿el dinero grande se mueve contigo o contra ti?" },
              { n: "03", icon: Newspaper, title: "Noticias", desc: "Lee solo los titulares capaces de mover la sesión, filtrados y ordenados." },
              { n: "04", icon: UserCheck, title: "Tu decisión", desc: "Con contexto, decides con tu plan y tu gestión de riesgo. Sin impulsos." },
            ].map(({ n, icon: Icon, title, desc }) => (
              <div key={n} className="bg-gray-900/40 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <Icon className="w-6 h-6 text-cyan-400" />
                  <span className="text-2xl font-bold text-gray-700">{n}</span>
                </div>
                <h3 className="font-semibold mb-2">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONFIANZA */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <p className="text-cyan-400 text-sm font-semibold tracking-widest text-center mb-2">TRANSPARENCIA</p>
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Un terminal de análisis, no una promesa mágica</h2>
        <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Check className="w-5 h-5 text-emerald-400" /> Qué es</h3>
            <p className="text-gray-400 text-sm leading-relaxed">Un espacio que centraliza noticias, sentimiento y movimientos de ballenas para organizar tu lectura del mercado antes de decidir.</p>
          </div>
          <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><X className="w-5 h-5 text-red-400" /> Qué no es</h3>
            <p className="text-gray-400 text-sm leading-relaxed">Ni un robot de trading, ni un vendedor de señales garantizadas, ni asesoramiento financiero personalizado.</p>
          </div>
          <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Eye className="w-5 h-5 text-cyan-400" /> Datos verificables</h3>
            <p className="text-gray-400 text-sm leading-relaxed">Todo viene de fuentes públicas: exchanges, DeFiLlama y agregadores de noticias. Sin cajas negras.</p>
          </div>
          <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-cyan-400" /> Tu cuenta, tu control</h3>
            <p className="text-gray-400 text-sm leading-relaxed">Sin tarjeta para la sesión gratuita. Tus datos de trading son solo tuyos y se quedan en tu cuenta.</p>
          </div>
        </div>
      </section>

      {/* PLANES */}
      <section id="planes" className="bg-[#0d0d0f] border-y border-gray-800">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="inline-flex w-full justify-center mb-2">
            <span className="bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold px-3 py-1.5 rounded-full">OFERTA DE LANZAMIENTO</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Empieza gratis, elige tu ritmo después</h2>
          <p className="text-gray-500 text-center mb-10">Durante el lanzamiento, el acceso completo al Terminal es gratuito con tu cuenta.</p>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {/* Gratuito */}
            <div className="bg-gray-900/40 border-2 border-cyan-500/50 rounded-2xl p-7 relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-600 text-white text-xs font-bold px-3 py-1 rounded-full">RECOMENDADO</span>
              <h3 className="font-semibold text-lg mb-1">Sesión Gratuita</h3>
              <p className="text-4xl font-bold mb-1">0€</p>
              <p className="text-gray-500 text-sm mb-5">para siempre</p>
              <ul className="space-y-2.5 text-sm text-gray-300 mb-7">
                {["Terminal completo en vivo", "Noticias ES/EN ilimitadas", "Whale tracker y flujos DeFi", "20 operaciones en tu dashboard"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />{f}</li>
                ))}
              </ul>
              <button onClick={onRegister} className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold py-3 rounded-xl transition-colors" data-testid="plan-free-cta">
                Crear cuenta gratuita
              </button>
            </div>
            {/* Mensual */}
            <div className="bg-gray-900/40 border border-gray-800 rounded-2xl p-7">
              <h3 className="font-semibold text-lg mb-1">Premium Mensual</h3>
              <p className="text-4xl font-bold mb-1">2,99€</p>
              <p className="text-gray-500 text-sm mb-5">/ mes</p>
              <ul className="space-y-2.5 text-sm text-gray-300 mb-7">
                {["Todo lo de la Sesión Gratuita", "Operaciones ilimitadas en el dashboard", "Todos los gráficos de análisis", "Exportar a PDF", "Soporte prioritario"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />{f}</li>
                ))}
              </ul>
              <button onClick={onRegister} className="w-full border border-gray-700 hover:border-cyan-500 text-gray-200 hover:text-cyan-400 font-semibold py-3 rounded-xl transition-colors">
                Empezar
              </button>
            </div>
            {/* Lifetime */}
            <div className="bg-gray-900/40 border border-gray-800 rounded-2xl p-7">
              <h3 className="font-semibold text-lg mb-1">Acceso de por Vida</h3>
              <p className="text-4xl font-bold mb-1">59€</p>
              <p className="text-gray-500 text-sm mb-5">pago único</p>
              <ul className="space-y-2.5 text-sm text-gray-300 mb-7">
                {["Todo lo Premium, para siempre", "Actualizaciones de por vida", "Sin renovaciones", "Precio de fundador"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />{f}</li>
                ))}
              </ul>
              <button onClick={onRegister} className="w-full border border-gray-700 hover:border-cyan-500 text-gray-200 hover:text-cyan-400 font-semibold py-3 rounded-xl transition-colors">
                Empezar
              </button>
            </div>
          </div>
          <p className="text-center text-gray-600 text-xs mt-6 flex items-center justify-center gap-1.5">
            <CreditCard className="w-3.5 h-3.5" /> Los pagos se procesan con Stripe. No almacenamos números de tarjeta.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-20">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Preguntas frecuentes</h2>
        <div className="space-y-3">
          {[
            { q: "¿Necesito pagar para usar el Terminal?", a: "No. Durante el periodo de lanzamiento, el acceso completo es gratuito: solo necesitas crear tu cuenta. Más adelante habrá planes con funciones avanzadas." },
            { q: "¿El Terminal da señales de compra o venta?", a: "No. Es una herramienta de lectura de mercado: noticias, sentimiento, ballenas y flujos. La decisión final es siempre tuya, con tu estrategia y tu gestión de riesgo." },
            { q: "¿De dónde salen los datos?", a: "De fuentes públicas y verificables: agregadores de noticias (DiarioBitcoin, CoinTelegraph, Decrypt), exchanges (MEXC, Binance, Kraken), DeFiLlama y alternative.me." },
            { q: "¿Es asesoramiento financiero?", a: "No. Todo el contenido es informativo y educativo. Invertir en criptomonedas conlleva riesgo; nunca inviertas más de lo que puedas permitirte perder." },
          ].map(({ q, a }) => (
            <details key={q} className="bg-[#0d0d0f] border border-gray-800 rounded-xl group">
              <summary className="flex items-center justify-between p-5 cursor-pointer font-medium hover:text-cyan-300 transition-colors">
                {q}
                <ChevronDown className="w-4 h-4 text-gray-500 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="px-5 pb-5 text-gray-400 text-sm leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="max-w-4xl mx-auto px-6 pb-20 text-center">
        <div className="bg-gradient-to-br from-cyan-900/40 to-blue-900/20 border border-cyan-500/30 rounded-2xl p-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">El mercado no espera.</h2>
          <p className="text-gray-400 mb-7">Crea tu cuenta gratuita y lee la sesión como un profesional.</p>
          <button onClick={onEnter} className="bg-cyan-600 hover:bg-cyan-500 text-white font-semibold px-8 py-3.5 rounded-xl text-lg transition-colors inline-flex items-center gap-2" data-testid="final-cta">
            Abrir el Terminal <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="TraceMetrics" className="h-7 w-auto opacity-70" />
            <span className="text-gray-600 text-xs">© 2026 TraceMetrics</span>
          </div>
          <p className="text-gray-600 text-xs text-center">Datos con fines informativos. No constituyen asesoramiento financiero.</p>
          <button onClick={onBack} className="text-gray-500 hover:text-cyan-400 text-sm transition-colors flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Volver a TraceMetrics
          </button>
        </div>
      </footer>
    </div>
  );
}

export default TerminalLandingPage;
